/**
 * Clients Tab Component
 * Now using reusable components for better maintainability
 */

import React, { useState } from 'react';
import { 
  Users, TrendingUp, AlertCircle, Eye, Calendar, RefreshCw, 
  ChevronUp, ChevronDown
} from 'lucide-react';
import { ClientPortfolio } from '../../services/api/portfolioApiService';
import {
  ClientCard,
  MetricCard,
  LoadingSpinner,
  EmptyState,
  Badge,
  SearchBar
} from './index';

interface ClientsTabProps {
  portfolios: ClientPortfolio[];
  onClientSelect: (clientId: string) => void;
  onAction: (action: string, clientId: string) => void;
  loading?: boolean;
}

const ClientsTab: React.FC<ClientsTabProps> = ({
  portfolios,
  onClientSelect,
  onAction,
  loading
}) => {
  const [sortField, setSortField] = useState<string>('total_value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Format currency
  const formatCurrency = (value: number | null) => {
    if (value === null) return 'N/A';
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number | null) => {
    if (value === null) return 'N/A';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Format date
  const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter portfolios
  const filteredPortfolios = portfolios.filter(p => {
    if (filterStatus === 'all') return true;
    return p.status === filterStatus;
  });

  // Filter with search
  const searchFilteredPortfolios = filteredPortfolios.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.client_name.toLowerCase().includes(query) ||
      p.client_id.toLowerCase().includes(query)
    );
  });

  // Sort portfolios
  const sortedPortfolios = [...searchFilteredPortfolios].sort((a, b) => {
    const aValue = a[sortField as keyof ClientPortfolio];
    const bValue = b[sortField as keyof ClientPortfolio];
    
    // Handle null values
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return sortDirection === 'asc' ? -1 : 1;
    if (bValue === null) return sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    const aStr = String(aValue);
    const bStr = String(bValue);
    return sortDirection === 'asc' 
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Calculate summary statistics
  const portfoliosWithHealth = portfolios.filter(p => p.health_score !== null && p.health_score !== undefined);
  const summary = {
    totalAUM: portfolios.reduce((sum, p) => sum + (p.total_value || 0), 0),
    averageHealth: portfoliosWithHealth.length > 0 
      ? portfoliosWithHealth.reduce((sum, p) => sum + (p.health_score || 0), 0) / portfoliosWithHealth.length
      : 0,
    needsAttention: portfolios.filter(p => p.status === 'attention').length,
    // TASK-723 (review 2026-07-29): this view's endpoint does not assess allocation drift —
    // the backend branch that could set 'rebalance' read a column it never selected, so this
    // count was structurally zero and rendered as "0 clients need rebalancing". A number
    // nobody computed is worse than no number. Counted anyway so a future drift-aware payload
    // shows up here instead of being silently ignored.
    needsRebalancing: portfolios.filter(p => p.status === 'rebalance').length,
    driftAssessed: portfolios.some(p => p.drift_assessed === true),
    totalClients: portfolios.length
  };

  // Get status color and icon
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'healthy':
        return { color: 'text-green-600 bg-green-50', icon: TrendingUp, label: 'Healthy' };
      case 'attention':
        return { color: 'text-yellow-600 bg-yellow-50', icon: AlertCircle, label: 'Needs Attention' };
      case 'rebalance':
        return { color: 'text-orange-600 bg-orange-50', icon: RefreshCw, label: 'Rebalance' };
      default:
        return { color: 'text-gray-600 bg-gray-50', icon: Users, label: 'Unknown' };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <LoadingSpinner size="lg" text="Loading client portfolios..." />
      </div>
    );
  }

  // Display in card grid mode if few clients, table mode if many
  const useCardView = sortedPortfolios.length <= 8;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Portfolio Overview"
          metrics={[
            {
              label: 'Total AUM',
              value: summary.totalAUM,
              format: 'currency',
              currency: 'CHF'
            },
            {
              label: 'Total Clients',
              value: summary.totalClients,
              format: 'number'
            }
          ]}
        />
        
        <MetricCard
          title="Health Metrics"
          metrics={[
            {
              label: 'Avg Health',
              value: summary.averageHealth,
              format: 'number',
              trend: summary.averageHealth >= 80 ? 'up' : summary.averageHealth >= 60 ? 'neutral' : 'down'
            }
          ]}
        />
        
        <MetricCard
          title="Attention Required"
          metrics={[
            {
              label: 'Need Attention',
              value: summary.needsAttention,
              format: 'number',
              trend: summary.needsAttention > 0 ? 'down' : 'neutral'
            }
          ]}
        />
        
        <MetricCard
          title="Rebalancing"
          metrics={[
            summary.driftAssessed
              ? {
                  label: 'Need Rebalancing',
                  value: summary.needsRebalancing,
                  format: 'number' as const,
                  trend: summary.needsRebalancing > 0 ? ('down' as const) : ('neutral' as const),
                }
              : {
                  // Not a zero. This view carries no drift assessment, and "0" would be a
                  // verdict on an analysis that never ran (TASK-722/723). My Book classifies
                  // drift canonically; this summary points there rather than guessing.
                  label: 'Need Rebalancing',
                  value: 'Not assessed here — see My Book',
                  trend: 'neutral' as const,
                },
          ]}
        />
      </div>

      {/* Client Portfolios - Card View for small datasets */}
      {useCardView && sortedPortfolios.length > 0 && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Client Portfolios</h3>
            <div className="flex items-center gap-4">
              {/* Search Bar */}
              <div className="w-64">
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search clients..."
                />
              </div>
              
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Filter:</span>
                <select 
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="healthy">Healthy</option>
                  <option value="attention">Needs Attention</option>
                  <option value="rebalance">Needs Rebalancing</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedPortfolios.map((portfolio) => (
              <ClientCard
                key={portfolio.client_id}
                id={portfolio.client_id}
                name={portfolio.client_name}
                portfolioValue={portfolio.total_value || 0}
                performance={portfolio.performance || 0}
                healthScore={portfolio.health_score || 0}
                currency="CHF"
                onSelect={() => onClientSelect(portfolio.client_id)}
                onAction={(id, action) => onAction(action, id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Client Portfolio Matrix - Table View for large datasets */}
      {!useCardView && (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Client Portfolios</h3>
              
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Filter:</span>
                <select 
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="healthy">Healthy</option>
                  <option value="attention">Needs Attention</option>
                  <option value="rebalance">Needs Rebalancing</option>
                </select>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="max-w-md">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by client name or ID..."
              />
            </div>
          </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('client_name')}
                >
                  <div className="flex items-center">
                    Client Name
                    {sortField === 'client_name' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Profile
                </th>
                <th 
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total_value')}
                >
                  <div className="flex items-center justify-end">
                    Total Value
                    {sortField === 'total_value' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('health_score')}
                >
                  <div className="flex items-center justify-center">
                    Health Score
                    {sortField === 'health_score' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th 
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedPortfolios.map((portfolio) => {
                const statusInfo = getStatusInfo(portfolio.status);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <tr key={portfolio.client_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {portfolio.client_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {portfolio.client_id.slice(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        label={portfolio.risk_profile}
                        variant="info"
                        size="sm"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {formatCurrency(portfolio.total_value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${
                        (portfolio.health_score || 0) >= 80 ? 'bg-green-100 text-green-800' :
                        (portfolio.health_score || 0) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        <span className="text-sm font-bold">
                          {portfolio.health_score || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      (portfolio.performance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatPercent(portfolio.performance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <Badge
                        label={statusInfo.label}
                        // 'unknown' (no health score, no drift assessment) is grey, not red:
                        // an unmeasured client is not a failing one.
                        variant={portfolio.status === 'healthy' ? 'success'
                          : portfolio.status === 'attention' ? 'warning'
                          : portfolio.status === 'unknown' ? 'neutral' : 'error'}
                        size="sm"
                        icon={<StatusIcon className="w-3 h-3" />}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(portfolio.updated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => onClientSelect(portfolio.client_id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Portfolio"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onAction('prepare-meeting', portfolio.client_id)}
                          className="text-green-600 hover:text-green-900"
                          title="Prepare Meeting"
                        >
                          <Calendar className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onAction('refresh', portfolio.client_id)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Refresh Data"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
          {sortedPortfolios.length === 0 && (
            <div className="p-6">
              <EmptyState
                type="no-results"
                title="No Portfolios Found"
                description="No portfolios found matching the selected criteria."
              />
            </div>
          )}
        </div>
      )}
      {/* Show empty state if no portfolios at all */}
      {sortedPortfolios.length === 0 && useCardView && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <EmptyState
            type="no-results"
            title="No Portfolios Found"
            description="No portfolios found matching the selected criteria."
          />
        </div>
      )}
    </div>
  );
};

export default ClientsTab;